// import { useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  // Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { Transition } from '../../components/Transition';
import { USER_ROLES } from '../../constants';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '../../constants/countryCodes';
// import { useAppDispatch } from '../../store';
// import { uniqBy } from 'lodash';
// import AutoComplete from '../../components/AutoComplete';
// import {
//   findSelectCountry,
//   searchSelectCountry,
// } from '../../store/selectCountry/api';
// import {
//   setSkip as setSkipCountry,
//   reset as resetCountry,
// } from '../../store/selectCountry/selectCountrySlice';
// import {
//   findSelectRegion,
//   searchSelectRegion,
// } from '../../store/selectRegion/api';
// import {
//   setSkip as setSkipRegion,
//   reset as resetRegion,
// } from '../../store/selectRegion/selectRegionSlice';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: (values: any) => Promise<boolean>;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<any>;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
  isAdminEditing?: boolean;
  refreshTable?: () => Promise<void>;
  // response: any;
  isCustomerCreation?: boolean;
}

const UsersFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
  isAdminEditing = false,
  refreshTable,
  isCustomerCreation = false
}) => {
  // Add state to track success message
  const [showSuccess, setShowSuccess] = useState(false);
  // Add state to track error message
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("An error occurred");
  // Add local submitting state to avoid UI issues
  const [localSubmitting, setLocalSubmitting] = useState(false);
  
  // Set Qatar as the default country code for new users
  useEffect(() => {
    // Only set default if it's a new user (no ID) and no country code is set yet
    if (!values.id && !values.countryCallingCode) {
      setFieldValue('countryCallingCode', DEFAULT_COUNTRY_CODE);
    }
    // Force role to 'user' for customer creation
    if (isCustomerCreation && !values.id && values.userRole !== 'user') {
      setFieldValue('userRole', 'user');
    }
  }, [values.id, values.countryCallingCode, setFieldValue]);
  
  // Handle form submission
  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous messages
    setShowSuccess(false);
    setShowError(false);
    setLocalSubmitting(true);
    
    try {
      console.log("Starting form submission");
      
      // Wait for handleSubmit to complete and check if successful
      const result = await handleSubmit(values);
      
      console.log("Form submission completed with result:", result);
      
      if (result === true) {
        // Show success and close only on success
        setShowSuccess(true);
        
        // Refresh the table data immediately
        if (refreshTable) {
          console.log("Refreshing table data after successful save");
          try {
            await refreshTable();
            console.log("Table refresh completed successfully");
          } catch (refreshError) {
            console.error("Error refreshing table:", refreshError);
          }
        }
        
        // Close form after a short delay
        setTimeout(() => {
          resetForm();
          onClose();
        }, 800);
      } else {
        // Keep dialog open on error
        setErrorMessage("Please fix the errors and try again.");
        setShowError(true);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // Show error message
      setErrorMessage("An unexpected error occurred. Please try again or contact support if the problem persists.");
      setShowError(true);
    } finally {
      setLocalSubmitting(false);
    }
  };
  
  // Handle closing success message
  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };
  
  // Handle closing error message
  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <>
      <Dialog
        fullWidth
        open={toggle}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          resetForm();
          onClose();
        }}
        aria-describedby='alert-dialog-slide-description'
        key={`uf`}
      >
        <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>
          {values?.id ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <form
          onSubmit={onFormSubmit}
          autoComplete='off'
        >
          <DialogContent dividers>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='firstName'
                  name='firstName'
                  label='First name'
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.firstName}
                  error={Boolean(errors.firstName && touched.firstName)}
                  helperText={
                    Boolean(errors.firstName && touched.firstName) &&
                    errors.firstName
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
              >
                <FormControl
                  fullWidth
                  error={Boolean(touched.gender && errors.gender)}
                >
                  <InputLabel>Gender</InputLabel>
                  <Select
                    id='gender'
                    name='gender'
                    value={values.gender || ''}
                    label='Gender'
                    onChange={(e) => setFieldValue('gender', e.target.value)}
                  >
                    <MenuItem value={'male'}>Male</MenuItem>
                    <MenuItem value={'female'}>Female</MenuItem>
                  </Select>
                  {Boolean(touched.gender && errors.gender) && (
                    <FormHelperText>{errors.gender}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='lastName'
                  name='lastName'
                  label='Last name'
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.lastName}
                  error={Boolean(errors.lastName && touched.lastName)}
                  helperText={
                    Boolean(errors.lastName && touched.lastName) &&
                    errors.lastName
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
              >
                <FormControl 
                  fullWidth
                  error={Boolean(errors.countryCallingCode && touched.countryCallingCode)}
                  disabled={isAdminEditing}
                >
                  <InputLabel>Country Code</InputLabel>
                  <Select
                    id="countryCallingCode"
                    name="countryCallingCode"
                    value={values.countryCallingCode || DEFAULT_COUNTRY_CODE}
                    label="Country Code"
                    onChange={(e) => {
                      setFieldValue('countryCallingCode', e.target.value);
                    }}
                  >
                    {COUNTRY_CODES.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.code} ({country.country})
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(errors.countryCallingCode && touched.countryCallingCode) && (
                    <FormHelperText>{errors.countryCallingCode}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={8}
              >
                <TextField
                  id='mobileNumber'
                  name='mobileNumber'
                  label='Mobile Number'
                  fullWidth
                  variant='outlined'
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    setFieldValue('mobileNumber', value);
                  }}
                  value={values.mobileNumber}
                  error={Boolean(errors.mobileNumber && touched.mobileNumber)}
                  helperText={
                    Boolean(errors.mobileNumber && touched.mobileNumber) &&
                    errors.mobileNumber
                  }
                  disabled={isAdminEditing}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='email'
                  name='email'
                  label={'E-mail'}
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.email}
                  error={Boolean(errors.email && touched.email)}
                  helperText={
                    Boolean(errors.email && touched.email) && errors.email
                  }
                  disabled={isAdminEditing}
                />
              </Grid>
              {/* <Grid
                item
                xs={12}
                sm={12}
              >
                <TextField
                  id='language'
                  name='language'
                  label={'Language'}
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.language}
                  error={Boolean(errors.language && touched.language)}
                  helperText={
                    Boolean(errors.language && touched.language) &&
                    errors.language
                  }
                />
              </Grid> */}
              {/* <Grid
                item
                xs={12}
                sm={12}
              >
                <AutoComplete
                  key={`co-${values?.countryId}`}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('countryId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('countryId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(touched.countryId && errors.countryId)}
                  helperText={
                    Boolean(touched.countryId && errors.countryId)
                      ? errors.countryId
                      : ''
                  }
                  fetchFunction={onScrollFetchCountry}
                  label={'Select Country'}
                  loading={isCountryLoading}
                  options={uniqueCountryData}
                  searchFunction={searchCountry}
                  value={
                    uniqueCountryData?.filter(
                      (country) => country.id === values.countryId
                    )?.[0] || null
                  }
                />
              </Grid> */}
              {/* <Grid
                item
                xs={12}
                sm={12}
              >
                <AutoComplete
                  key={`re-${values?.regionId}`}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('regionId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('regionId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(touched.regionId && errors.regionId)}
                  helperText={
                    Boolean(touched.regionId && errors.regionId)
                      ? errors.regionId
                      : ''
                  }
                  fetchFunction={onScrollFetchRegion}
                  label={'Select Region'}
                  loading={isRegionLoading}
                  options={uniqueRegionData}
                  searchFunction={searchRegion}
                  value={
                    uniqueRegionData?.filter(
                      (region) => region.id === values.regionId
                    )?.[0] || null
                  }
                />
              </Grid> */}
              {!isCustomerCreation && (
              <Grid
                item
                xs={12}
                sm={12}
              >
                <FormControl
                  fullWidth
                  error={Boolean(touched.userRole && errors.userRole)}
                  disabled={isAdminEditing}
                >
                  <InputLabel>Select User Role</InputLabel>
                  <Select
                    id='userRole'
                    name='userRole'
                    value={values.userRole}
                    onChange={(e) => {
                      setFieldValue('userRole', e.target.value);
                    }}
                    label='Select User Role'
                  >
                    {USER_ROLES.filter(role => {
                      // Show admin role only when adding a new user, not when editing
                      if (role.value === 'admin') {
                        // Only show admin option when creating a new user (values.id is empty)
                        return !values.id;
                      }
                      // Show all other roles
                      return true;
                    }).map((role) => {
                      return (
                        <MenuItem
                          key={role?.value}
                          value={role?.value}
                          sx={{
                            textTransform: 'capitalize',
                          }}
                        >
                          {role?.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {Boolean(touched.userRole && errors.userRole) && (
                    <FormHelperText>{errors.userRole ?? ''}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              )}
              {values?.id && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='activeState'
                        onChange={(e) =>
                          setFieldValue('activeState', e.target.checked)
                        }
                        checked={values.activeState}
                      />
                    }
                    label={'Active Status'}
                  />
                </Grid>
              )}
              {values?.userRole === 'admin' && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color='primary'
                        name='allowNotification'
                        onChange={(e) =>
                          setFieldValue('allowNotification', e.target.checked)
                        }
                        checked={values.allowNotification}
                      />
                    }
                    label={'Allow WhatsApp Notifications'}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                resetForm();
                onClose();
              }}
              variant='outlined'
            >
              {'Cancel'}
            </Button>
            <LoadingButton
              type='submit'
              loading={isSubmitting || localSubmitting}
              variant='contained'
            >
              {'Save'}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Success message */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={1500} 
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          Saved successfully!
        </Alert>
      </Snackbar>
      
      {/* Error message */}
      <Snackbar 
        open={showError} 
        autoHideDuration={3000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UsersFormModal;
